import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, jsonBody } from "../lib/api";

export interface User {
  id: string;
  email: string;
  displayName: string | null;
  defaultResearchPreference: "grounded" | "intake_only";
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  register(email: string, password: string, displayName: string): Promise<void>;
  logout(): Promise<void>;
  startDemo(scenario?: "conditional_go" | "no_go"): Promise<string>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api<{ user: User | null }>("/api/v1/auth/me");
      setUser(data.user);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    refresh,
    async login(email, password) {
      const data = await api<{ user: User }>("/api/v1/auth/login", {
        method: "POST",
        ...jsonBody({ email, password }),
      });
      setUser(data.user);
    },
    async register(email, password, displayName) {
      const data = await api<{ user: User }>("/api/v1/auth/register", {
        method: "POST",
        ...jsonBody({ email, password, displayName }),
      });
      setUser(data.user);
    },
    async logout() {
      await api("/api/v1/auth/logout", { method: "POST" });
      setUser(null);
    },
    async startDemo(scenario = "conditional_go") {
      const data = await api<{ user: User; project: { id: string } }>("/api/v1/demo/start", {
        method: "POST",
        ...jsonBody({ scenario }),
      });
      setUser(data.user);
      return data.project.id;
    },
  }), [loading, refresh, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
